/**
 * 命名空间化的 ID。
 *
 * 所有跨数据包引用的东西（卡牌、效果、事件、动作）都是 `包名:局部名` 的形式：
 *
 * ```
 * vanilla:Exception     vanilla 包里的 "Exception"
 * vanilla:Powerless1    vanilla 包里的无效牌 1 号
 * imploding:ImplodingKitten
 * ```
 *
 * 好处：
 * - **永不撞名** —— 扩展包各写各的，不用协调
 * - **一看日志就知道是谁贡献的**
 * - 类型上是 branded string，`PackId` 和 `CardTitle` 不会互相传错
 *
 * 这是「未来加扩展包不困难」的第一块地基（见 `docs/STATUS.md`）。
 */

declare const PackIdBrand: unique symbol;
declare const CardTitleBrand: unique symbol;
declare const EffectIdBrand: unique symbol;
declare const PlayerIdBrand: unique symbol;
declare const CardUidBrand: unique symbol;

/** 数据包标识，例如 `core` / `imploding` */
export type PackId = string & { readonly [PackIdBrand]: true };

/**
 * 牌名（命名空间化）。
 *
 * ⚠️ 连击判定用的就是它 —— **同名牌才能凑一对**，
 * 所以 5 张无效牌是 5 个不同牌名，不能互相凑（`docs/rules.md` §6.3）。
 */
export type CardTitle = string & { readonly [CardTitleBrand]: true };

/** 卡牌效果的实现 ID。卡定义里只存这个字符串，实现由注册表查（见 `pack.ts`） */
export type EffectId = string & { readonly [EffectIdBrand]: true };

/** 玩家标识 */
export type PlayerId = string & { readonly [PlayerIdBrand]: true };

/**
 * 一张**卡牌实例**的编号。
 *
 * 同一个牌名可以有很多张（比如 4 张 Exception），
 * 所以手牌/牌堆里存的是 uid，再经注册表查它的牌名。
 */
export type CardUid = number & { readonly [CardUidBrand]: true };

export const packId = (id: string): PackId => id as PackId;
export const playerId = (id: string): PlayerId => id as PlayerId;
export const cardUid = (n: number): CardUid => n as CardUid;

export const cardTitle = (pack: PackId, local: string): CardTitle =>
  `${pack}:${local}` as CardTitle;

export const effectId = (pack: PackId, local: string): EffectId =>
  `${pack}:${local}` as EffectId;

/** 取出 `vanilla:Exception` 里的 `vanilla` */
export function namespaceOf(id: string): string {
  const i = id.indexOf(':');
  return i < 0 ? '' : id.slice(0, i);
}

